"use client";

import { useQuery } from "@apollo/client/react";
import { motion } from "motion/react";
import type { BaseSyntheticEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import { useStableSearchParams } from "@/components/app/dialogs/dialog-provider";
import {
  DialogDrawerDescription,
  DialogDrawerTitle,
} from "@/components/shared/DialogDrawer";
import { PostImageCarousel } from "@/components/shared/post-image/post-image-carousel";
import { Spinner } from "@/components/shared/spinner";
import type { Post as PostType } from "@/graphql/codegen/graphql";
import { PostByPostHashDocument } from "@/graphql/codegen/graphql";
import { useMobile } from "@/hooks/use-mobile";
import { buildMediaMeta } from "@/utils/post-utils";
import type { PostMediaMeta } from "@/utils/types";

export type ImageViewerDialogContentProps = {
  alt?: string;
  index?: number | string;
  mediaMeta?: PostMediaMeta;
  optimize?: boolean;
  postHash?: string;
  setOpen?: (open: boolean) => void;
  sourceLink?: string;
  url?: string;
  urls?: string[];
};

const IMG_LOADING_DELAY = 300;
const IMAGE_VIEWER_DRAG_CLOSE_DISTANCE = 100;

export default function ImageViewerDialogContent({
  alt,
  index,
  mediaMeta,
  optimize = true,
  postHash,
  setOpen,
  sourceLink,
  url,
  urls,
}: ImageViewerDialogContentProps) {
  const searchParams = useStableSearchParams();
  const { hasMouse } = useMobile();
  const activePostHash = postHash ?? searchParams.get("postHash") ?? "";
  const activeSourceLink =
    sourceLink ?? searchParams.get("sourceLink") ?? undefined;
  const activeAlt = alt ?? searchParams.get("alt") ?? "Image";
  const activeIndexValue = index ?? searchParams.get("index") ?? "0";
  const parsedIndex = Number(activeIndexValue);
  const initialSelectedIndex =
    Number.isFinite(parsedIndex) && parsedIndex >= 0 ? parsedIndex : 0;
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const { data, loading } = useQuery(PostByPostHashDocument, {
    skip: !activePostHash,
    variables: {
      postHash: activePostHash,
    },
  });
  const post = useMemo(
    () => (data?.postByPostHash as PostType | null | undefined) ?? null,
    [data?.postByPostHash],
  );
  const resolvedUrls = useMemo(
    () => (post?.imageUrls?.filter(Boolean) as string[] | undefined) ?? [],
    [post?.imageUrls],
  );
  const resolvedMediaMeta = useMemo(
    () =>
      activePostHash
        ? buildMediaMeta(post?.extraData ?? {})
        : (mediaMeta ?? {}),
    [activePostHash, mediaMeta, post?.extraData],
  );
  const activeUrls = urls?.length ? urls : resolvedUrls;
  const selectedIndex = Math.max(
    0,
    Math.min(initialSelectedIndex, Math.max(activeUrls.length - 1, 0)),
  );
  const activeUrl =
    url ?? searchParams.get("url") ?? activeUrls[selectedIndex] ?? "";
  const isGallery = activeUrls.length > 0;

  useEffect(() => {
    if (isGallery) {
      setStatus("ready");
      return;
    }

    if (activePostHash && loading) {
      setStatus("loading");
      return;
    }

    if (activePostHash && !loading) {
      setStatus("error");
      return;
    }

    if (!activeUrl) {
      setStatus("error");
      return;
    }

    setStatus("loading");

    const img = new Image();
    img.src = activeUrl;
    img.onload = () => {
      setTimeout(() => {
        setStatus("ready");
      }, IMG_LOADING_DELAY);
    };
    img.onerror = () => {
      setStatus("error");
    };
  }, [activeUrl, isGallery]);

  return (
    <>
      <DialogDrawerTitle className="hidden">{activeAlt}</DialogDrawerTitle>
      <DialogDrawerDescription className="hidden">
        {activeAlt}
      </DialogDrawerDescription>
      {isGallery && (
        <motion.div
          className="focus-image-viewer-fullscreen flex size-full items-center justify-center"
          drag={hasMouse ? false : "y"}
          dragConstraints={hasMouse ? undefined : { top: 0, bottom: 0 }}
          onDragEnd={(
            _: BaseSyntheticEvent,
            info: {
              point: {
                y: number;
              };
            },
          ) => {
            if (!hasMouse && info.point.y > IMAGE_VIEWER_DRAG_CLOSE_DISTANCE) {
              setOpen?.(false);
            }
          }}
        >
          <PostImageCarousel
            fullscreen
            imageContainerClassName="rounded-none border-0 bg-transparent"
            initialSelectedIndex={selectedIndex}
            keyboardNavigation
            mediaMeta={resolvedMediaMeta}
            optimize={optimize}
            postHash={activePostHash || undefined}
            singleImageClassName="rounded-none"
            sourceLink={activeSourceLink}
            urls={activeUrls}
          />
        </motion.div>
      )}
      {!isGallery && status === "loading" && (
        <div className="flex items-center justify-center p-16">
          <Spinner size={32} delay={1000} />
        </div>
      )}
      {!isGallery && status === "ready" && activeUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={activeUrl}
          alt={activeAlt}
          className="max-h-[calc(90vh-64px)] max-w-[calc(90vw-56px)] rounded-sm"
        />
      )}
      {!isGallery && status === "error" && <p className="p-16">No image</p>}
    </>
  );
}
